import React, {
	useEffect,
	useRef,
	useState,
	cloneElement,
	isValidElement,
	Children,
} from 'react'
import dynamic from 'next/dynamic'
import type {ReactNode, JSX} from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {Block} from '@/_new-code/services/kontent-ai/types'
import {useWindowSize} from '@/_new-code/utilities/hooks/use-window-size'
import {GridCards} from '@/_new-code/products/flexible-web-toolkit/components/grid-cards'
import {pushToDataLayer} from '@/utils/analytics'
import {getContainerClass} from '../helpers'
import styles from './dynamicCardsLister.module.css'
import './slickSlider.module.css'

// Keep SSR disabled so react-slick never renders on server.
const Slider = dynamic(() => import('react-slick'), {ssr: false})

interface DynamicCardsListerProps {
	autoplay?: boolean
	autoplaySpeed?: number
	className?: string
	children: ReactNode
	columns?: number
	hideSliderUI?: boolean
	isSliderOnMobile?: boolean
	isEqualHeightContent?: boolean
	ofLabel?: string
	showAsSlider?: boolean
	sliderNavText?: string
}

interface SlickSliderRef {
	slickGoTo: (index: number) => void
}

const DynamicCardsLister = ({
	className = '',
	children,
	columns = 3,
	isEqualHeightContent = false,
	isSliderOnMobile = false,
	hideSliderUI = false,
	ofLabel,
	showAsSlider = false,
	sliderNavText = 'slider nav',
	autoplay = false,
	autoplaySpeed = 2000,
}: DynamicCardsListerProps): JSX.Element => {
	const normalizedChildren = Children.toArray(children)
		.filter(isValidElement)
		.map((child) => child as JSX.Element)
	const [slidesToShow, setSlidesToShow] = useState(1)
	const [slideIndex, setSlideIndex] = useState(0)
	const [replaceDotsWithRange, setReplaceDotsWithRange] = useState(false)
	const sliderRef = useRef<SlickSliderRef | null>(null)
	const [isMounted, setIsMounted] = useState(false)

	const {
		isMobile = false,
		isTablet = false,
		width = 0,
	} = useWindowSize() as {
		isMobile: boolean
		isTablet: boolean
		width: number
	}

	useEffect(() => {
		setIsMounted(true)
	}, [])

	useEffect(() => {
		let columnsToShow = 1

		if (isMobile) {
			columnsToShow = 1
		} else if (isTablet || width < 1100) {
			columnsToShow = columns > 2 ? 2 : columns
		} else if (width >= 1100) {
			columnsToShow = columns
		}

		setSlidesToShow(columnsToShow)
		setReplaceDotsWithRange(
			(isMobile && normalizedChildren.length > 4) ||
				normalizedChildren.length > 8
		)
	}, [width, isMobile, isTablet, normalizedChildren.length, columns])

	const settings = {
		dots: !replaceDotsWithRange && !hideSliderUI,
		arrows: !hideSliderUI,
		autoplay,
		autoplaySpeed,
		infinite: true,
		speed: 500,
		slidesToShow,
		slidesToScroll: 1,
		adaptiveHeight: false,
		dotsClass: 'slick-dots',
		variableWidth: true,
		beforeChange: (current: number, next: number) => {
			const direction = next - current
			const targetIndex =
				(current + direction + normalizedChildren.length) %
				normalizedChildren.length
			if (!autoplay) {
				pushToDataLayer({
					event: 'carousel_click',
					carousel_name:
						(
							normalizedChildren[targetIndex]?.props as {
								block?: {title?: string}
							}
						).block?.title ?? sliderNavText,
					carousel_action:
						direction === 1 ? 'Click Right' : 'Click Left',
				})
			}
			setSlideIndex(next)
		},
	}

	const wantsSlider = showAsSlider || (isMobile && isSliderOnMobile)
	const shouldRenderSlider = wantsSlider && isMounted

	// IMPORTANT: To avoid hydration warnings (notably data-index) we ensure
	// the server and the pre-hydration client both render the SAME placeholder
	// markup for slider mode. We do NOT render the grid fallback when a slider
	// is ultimately desired; instead we render an empty container that is
	// replaced after mount. This prevents React from diffing drastically
	// different trees (grid vs cloned slick slides) which triggers warnings.
	if (wantsSlider && !shouldRenderSlider) {
		return <div className={className} suppressHydrationWarning />
	}

	const pagination = ofLabel
		? ofLabel
				.replace(/{{X}}/, `${slideIndex + 1}`)
				.replace(/{{Y}}/, `${normalizedChildren.length}`)
		: `${slideIndex + 1} of ${normalizedChildren.length}`

	return (
		<div className={className}>
			{shouldRenderSlider ? (
				<div
					className={`${styles.cardCarousel} ${
						replaceDotsWithRange && !hideSliderUI
							? styles.customNav
							: ''
					} relative mx-auto max-w-sm md:max-w-none`}
				>
					<Slider
						className={isEqualHeightContent ? 'flex-slider' : ''}
						// @ts-expect-error ref typing from dynamic import
						ref={sliderRef}
						{...settings}
					>
						{normalizedChildren.map((child) => (
							<div className="h-full p-4" key={child.key}>
								{cloneElement(child, {
									className: `${
										(child.props as {className?: string})
											.className || ''
									} h-full`,
								})}
							</div>
						))}
					</Slider>
					{replaceDotsWithRange && !hideSliderUI ? (
						<div className={styles.rangeBar}>
							<input
								max={normalizedChildren.length - 1}
								min={0}
								onChange={(e) =>
									sliderRef.current?.slickGoTo(
										Number(e.target.value)
									)
								}
								title={sliderNavText}
								type="range"
								value={slideIndex}
							/>
							<span>{pagination}</span>
						</div>
					) : null}
				</div>
			) : (
				<GridCards col={columns}>{normalizedChildren}</GridCards>
			)}
		</div>
	)
}

interface ListerProps {
	columns?: number
	showAsSlider?: boolean
	isSliderOnMobile?: boolean
	snippetContainerResize?: string
	children?: ReactNode
	ofLabel?: string
	autoplay?: boolean
	autoplaySpeed?: number
}

const ListerDefaults = {
	columns: 3,
	showAsSlider: false,
	isSliderOnMobile: false,
	children: [] as ReactNode,
}

export const Lister = ({
	columns,
	showAsSlider,
	isSliderOnMobile,
	snippetContainerResize,
	children,
	ofLabel,
	autoplay = false,
	autoplaySpeed = 2000,
}: ListerProps = ListerDefaults): JSX.Element => {
	const [windowWidth, setWindowWidth] = useState(0)
	const containerSize = getContainerClass(snippetContainerResize ?? '')
	const size: {isMobile: boolean | undefined} = useWindowSize()

	useEffect(() => {
		if (typeof window !== 'undefined') setWindowWidth(window.innerWidth)
	}, [])

	return (
		<DynamicCardsLister
			autoplay={autoplay}
			autoplaySpeed={autoplaySpeed}
			className={
				windowWidth < 768 || size.isMobile ? 'w-full' : containerSize
			}
			columns={columns}
			hideSliderUI={autoplay}
			isEqualHeightContent
			isSliderOnMobile={isSliderOnMobile}
			ofLabel={ofLabel}
			showAsSlider={showAsSlider}
		>
			{children}
		</DynamicCardsLister>
	)
}

export type CardListerContentItem = IContentItem<{
	items: Elements.LinkedItemsElement
	columns: Elements.MultipleChoiceElement
	slider: Elements.MultipleChoiceElement
	mobileSlider: Elements.MultipleChoiceElement
	snippetContainerResize: Elements.MultipleChoiceElement
	ofLabel: Elements.TextElement
}>

export const CardListerBlock: Block<CardListerContentItem> = ({
	block,
	BlockMapper,
	...context
}) => {
	return (
		<Lister
			columns={Number(block.elements.columns[0]?.name ?? 3)}
			isSliderOnMobile={
				block.elements.mobileSlider[0]?.codename === 'yes'
			}
			ofLabel={block.elements.ofLabel}
			showAsSlider={block.elements.slider[0]?.codename === 'yes'}
			snippetContainerResize={
				block.elements.snippetContainerResize[0]?.codename
			}
		>
			{block.elements.items.map((item) => (
				<BlockMapper
					blocks={[item]}
					key={item.system.id}
					{...context}
				/>
			))}
		</Lister>
	)
}
